import { useEffect, useState } from "react";

const SESSION_KEY = "ph_splash_shown";
const HOLD_MS_DEFAULT = 1000;
const HOLD_MS_REDUCED = 200;
const FADE_MS = 380;

/**
 * useSplashLifecycle — orchestrates the first-visit splash overlay.
 * Handles sessionStorage persistence, reduced-motion, hold, fade, unmount.
 * Returns:
 *   visible   — mount the overlay
 *   fadingOut — apply the fade transition
 */
export function useSplashLifecycle() {
  const [visible, setVisible] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      return !sessionStorage.getItem(SESSION_KEY);
    } catch (_err) {
      // sessionStorage unavailable — show splash, skip persistence
      return true;
    }
  });
  const [fadingOut, setFadingOut] = useState(false);

  useEffect(() => {
    if (!visible) return undefined;

    try {
      sessionStorage.setItem(SESSION_KEY, "1");
    } catch (_err) {
      // sessionStorage unavailable — splash will simply replay next visit
    }

    const prefersReduced =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const hold = prefersReduced ? HOLD_MS_REDUCED : HOLD_MS_DEFAULT;

    const fadeTimer = setTimeout(() => setFadingOut(true), hold);
    const unmountTimer = setTimeout(() => setVisible(false), hold + FADE_MS);

    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(unmountTimer);
    };
  }, [visible]);

  return { visible, fadingOut };
}
